/**
 * Single source of truth for robotics vision examples
 * 
 * Each example has a unique ID that links:
 * - The prompt configuration
 * - The image file in public/images/solutions/robot-vision/{id}.[jpeg|png]
 * - The generated results in results.ts
 * 
 * These examples demonstrate general robotics vision capabilities using real-world scenarios.
 * This ensures type safety and prevents misalignment.
 * 
 * IMAGE GENERATION PROMPTS:
 * Use these prompts with DALL-E, Midjourney, or Stable Diffusion to generate example images:
 * 
 * dirty_laundry:
 *   "First-person POV from a humanoid robot's perspective looking down at a pile of dirty 
 *    laundry on a bedroom floor. The view shows the robot's hands/arms at the bottom edges 
 *    of the frame. Scattered clothing items include wrinkled t-shirts, jeans, socks, a towel, 
 *    some items are on the floor near a laundry basket. Natural indoor lighting from a window, 
 *    realistic home environment with a bed visible in background, photo-realistic style, 
 *    slight lens distortion typical of robot camera"
 * 
 * dirty_kitchen_sink:
 *   "First-person POV from a humanoid robot's perspective looking at a kitchen sink full of 
 *    dirty dishes. The view shows the robot's mechanical hands gripping the counter edge at 
 *    bottom of frame. Sink contains stacked plates with food residue, bowls, cups, silverware, 
 *    a frying pan with dried food. Running water faucet, dish soap bottle nearby, natural 
 *    kitchen lighting, modern home kitchen setting, photo-realistic style, camera mounted 
 *    at robot head height approximately 5-6 feet"
 */

export interface ExampleConfig {
  /** Unique identifier - used for image filename and result lookup */
  id: string;
  /** Display title */
  title: string;
  /** Short description explaining the use case */
  description: string;
  /** Prompt to send to the API for robot decision-making */
  prompt: string;
  /** Moondream skill used (query with reasoning for robot understanding) */
  skill: 'query' | 'caption' | 'detection' | 'point';
  /** Whether to use reasoning mode */
  useReasoning: boolean;
}

/**
 * Define all examples here.
 * Images should be in: public/images/solutions/robot-vision/{id}.[jpeg|png]
 */
export interface ExampleConfigWithImage extends ExampleConfig {
  /** The actual image file to use */
  imageFile: string;
}

export const exampleConfigs: ExampleConfigWithImage[] = [
  {
    id: 'laundry-dirty',
    imageFile: 'robot_laundry_example',
    title: 'Detect objects that need to be picked up',
    description: 'Robot detects and localizes items in the environment to understand what needs attention and where to act.',
    prompt: 'dirty laundry',
    skill: 'detection',
    useReasoning: false,
  },
  {
    id: 'laundry-basket',
    imageFile: 'robot_laundry_example',
    title: 'Target where to place items',
    description: 'Robot identifies the location of tools and containers needed to complete the task.',
    prompt: 'laundry basket',
    skill: 'detection',
    useReasoning: false,
  },
  {
    id: 'dishes-dirty',
    imageFile: 'robot_dishes_example',
    title: 'Work Area Assessment',
    description: 'Robot detects all items requiring processing in the work area.',
    prompt: 'dirty dishes',
    skill: 'detection',
    useReasoning: false,
  },
  {
    id: 'dishes-sponge',
    imageFile: 'robot_dishes_example',
    title: 'Tool Localization',
    description: 'Robot locates cleaning tools needed to execute the task.',
    prompt: 'sponge',
    skill: 'detection',
    useReasoning: false,
  },
] as const;

